import { Injectable } from '@angular/core';
import { HTTPWrapperContext, NormalizedResponse, REQUEST_METHODS, RequestConfigImplementation, XHR_STRATEGIES } from '@ptl/xhr';
import { brandValues } from '@ptl/brand';
import { County, CountyListResponse, PtlGlossaryEntry } from '@ptl/global-components';
import { ConsumerEffectiveDate, EffectiveDateRetrievalOptions, FamilyFormService } from '../../../form-wrappers/family-form/services/family-form.service';
import moment from 'moment';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';
import { IndividualMarketStateStructure, RatingInfo } from '../../../form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { OnExchangeEligibilityStateStructure } from '../../../on-exchange-enrollment/reducers/eligibility.reducer';
import { PRODUCT_GROUP_NAMES_INDIVIDUAL } from '../../../plan-selection/enums/product-groups.enum';
import { IndividualPlansService } from '../../../plan-selection/plan-summary-list/plan-service-strategies/individual-plans.service';
import { DropdownOption, EXCEL_ENROLLMENT_TYPES, EXCEL_PERSON_TYPES, MassUploadEntryConfig, MassUploadGridConfiguration } from '../interfaces/mass-upload-grid.interface';
import { APPLICANT_RELATION } from '../../../plan-selection/services/interfaces/shared/plan-demographics/applicant.interface';
import { PlanUtilityService } from '../../../plan-selection/plan-summary-list/plan-utility.service';
import { PlanServiceResponse } from '../../../plan-selection/interfaces/plan-service-response.interface';
import { CommonUtilityService } from '../../../../shared/utility-services/common-utility.service';
import { MassUploadGridDataHelperService } from './mug-data-helper.service';
import { Person } from '../../../form-wrappers/form-components/individual-market-persons/interfaces/person.interface';
import { SUBMISSION_DATE_FORMAT } from '../interfaces/mug-submission.interface';
import { PlanAttributes, SummaryView } from '../../../plan-selection/interfaces/plan-attributes.interface';
import { Product } from '../../../plan-selection/interfaces/product.interface';
import { HttpHeaders } from '@angular/common/http';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';

@Injectable({
  providedIn: 'root'
})
export class MassUploadGridService {
  effectiveDateSoftCache: {[key: string]: ConsumerEffectiveDate} = {};
  individualPlansSoftCache: {[key: string]: PlanServiceResponse} = {};

  constructor(
    private familyFormService: FamilyFormService,
    private individualPlansService: IndividualPlansService,
    private planUtilityService: PlanUtilityService,
    private commonUtilityService: CommonUtilityService,
    private gridHelper: MassUploadGridDataHelperService,
    private httpClient: HTTPWrapperContext
  ) {
    
  }

  async loadJsonExcelMap(url: string): Promise<MassUploadGridConfiguration> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: url,
      headers: new HttpHeaders({})
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.MOCK);
    return await this.httpClient.makeRequest(requestConfig);
  }
  
  /**
   * 
   * @param entryList - A list of all the entries to be processed for a single row of data
   * 
   * @description - This method serves to preload all asynchronous service calls in order, waiting
   * for each row one by one. This will reduce concurrent load on the network, and populate a cache that
   * will also reduce total network calls
   */
  async preloadServiceValues(entryList: MassUploadEntryConfig[]) {
    let stateId = entryList[0].massUploadAttributes.stateId;
    let personType = this.gridHelper.getPersonTypeByStateId(stateId);

    let zipValue = this.gridHelper.getZipCodeByStateId(stateId);
    if (!zipValue || zipValue.length != 5) {
      return;
    }

    try {
      let countyResponse = await this.fetchIndividualCounty(zipValue);
      if (personType === EXCEL_PERSON_TYPES.SUBSCRIBER && countyResponse?.payload?.countyList?.length) {
        let countyName = this.gridHelper.getCountyNameByStateId(stateId);
        let matchingCounty = countyResponse.payload.countyList.filter((county) => county.county.toUpperCase() === countyName?.toUpperCase())[0];

        // Don't need to do anything with these responses, just preloading the service for performance/cache reasons
        if (matchingCounty) {
          await this.loadEffectiveDatesForRegion(matchingCounty.region, this.gridHelper.getEnrollmentTypeByStateId(stateId));
          await this.loadIndividualPlans(zipValue, matchingCounty, stateId);
        }
      }
    }
    catch(e) {
      console.error('Error preloading service values', e);
    }
  }

  fetchIndividualCounty(zipCode: string): Promise<NormalizedResponse<CountyListResponse>> {
    return this.commonUtilityService.fetchCounty(zipCode, "IND");
  }

  async loadEffectiveDatesForRegion(region: string, enrollmentType: EXCEL_ENROLLMENT_TYPES) {
    let effectiveDates: DropdownOption[] = [];

    const options: EffectiveDateRetrievalOptions = {
      isSep: enrollmentType === EXCEL_ENROLLMENT_TYPES.SEP,
      region: region,
      offExchangeIndicator: 'true',
      isDental: false,
      isBeb: false
    };
  
    const effectiveDateResponse = await this.loadEffectiveDates(options);
    if (effectiveDateResponse?.payload?.consumerEffectiveDateSingle?.length) {
      effectiveDates = effectiveDateResponse.payload.consumerEffectiveDateSingle.map((ceds) => {
        return {
          label: moment.tz(ceds.displayEffDate, MomentTimeZoneEnum[environment.configs.siteTimeZone]).format(SUBMISSION_DATE_FORMAT),
          value: moment.tz(ceds.displayEffDate, MomentTimeZoneEnum[environment.configs.siteTimeZone]).format(SUBMISSION_DATE_FORMAT)
        }
      });
    }

    return effectiveDates;
  }

  private async loadEffectiveDates(options: EffectiveDateRetrievalOptions): Promise<ConsumerEffectiveDate> {
    const cacheKey = JSON.stringify(options);

    if (this.effectiveDateSoftCache[cacheKey]) {
      return this.effectiveDateSoftCache[cacheKey];
    }

    try {
      this.effectiveDateSoftCache[cacheKey] = await this.familyFormService.getEffectiveDates(options);
    }
    catch(e) {
      console.error('Error fetching effective dates: ', e);
      //this.effectiveDateSoftCache[cacheKey] = {} as ConsumerEffectiveDate;
    }
    
    return this.effectiveDateSoftCache[cacheKey] || {} as ConsumerEffectiveDate;
  }

  async loadIndividualPlans(zipCode: string, county: County, stateId: number): Promise<PlanServiceResponse> {
    const productGroup: PRODUCT_GROUP_NAMES_INDIVIDUAL = 'Off-Exchange';
    const ddValue = brandValues.filter((brand) => brand.region === county.region)[0].ddToken;
    const zipCountyState: ZipCountyStateStructure = {
      zipCode: zipCode,
      county: county
    };
    const individualMarketState: IndividualMarketStateStructure = {
      childOnlyPolicy: "No",
      ratingInfo: this.buildGenericRatingInfoPayload(county, stateId)
    } as IndividualMarketStateStructure;

    // Only needed for On-Exchange, should we implement that in the future
    const onExchangeEligibilityState: OnExchangeEligibilityStateStructure = {
      currentProspectId: '',
      prospects: []
    };

    // Year value needs to be based on what effective date year is selected
    let effectiveDate = this.gridHelper.getEffectiveDateByStateId(stateId);
    if (!effectiveDate || typeof effectiveDate !== 'string') {
      return {} as PlanServiceResponse;
    }
    individualMarketState.ratingInfo.effectiveDate = effectiveDate;

    let initialPayload = this.individualPlansService.createPayloadWithValues(
      productGroup,
      zipCountyState,
      ddValue,
      individualMarketState,
      onExchangeEligibilityState
    );

    // Massage payload object to fit into expected interface type for plans service
    let servicePayload = Object.assign({productId: ''}, initialPayload);
    servicePayload.year = effectiveDate.split('/')[2];

    let cacheKey = JSON.stringify(servicePayload);
    if (this.individualPlansSoftCache[cacheKey]) {
      return this.individualPlansSoftCache[cacheKey];
    }

    try {
      this.individualPlansSoftCache[cacheKey] = await this.planUtilityService.getPlansForPlanDetailsIndividual(servicePayload);
    }
    catch(e) {
      this.individualPlansSoftCache[cacheKey] = {} as PlanServiceResponse;
    }

    return this.individualPlansSoftCache[cacheKey];
  }

  parsePlansAsDropdownValues(plansResponse: PlanServiceResponse): DropdownOption[] {
    if (!plansResponse?.plans?.['Off-Exchange']) {
      return [];
    }

    let options = [];
    let plansObj = plansResponse.plans['Off-Exchange'];
    Object.keys(plansObj).forEach((key) => {
      let plan = plansObj[key] as Product<PlanAttributes, SummaryView>;

      options.push({
        label: `${plan.productName} - ${plan.attributes.HIOS_ID}`,
        value: plan
      });
    });

    options.sort((a:{value:Product<PlanAttributes, SummaryView>},b:{value:Product<PlanAttributes, SummaryView>}) => {
      return a.value.productName?.localeCompare(b.value.productName);
    });

    return options;
  }

  buildGenericRatingInfoPayload(county: County, stateId: number): RatingInfo {
    let ratingInfo: RatingInfo = {
      subscriber: {},
      'spousal-partner': {},
      dependents: {}
    } as RatingInfo;
    
    ratingInfo.subscriber[0] = this.getGenericSubscriber();

    if (this.gridHelper.getPersonTypeByStateId(stateId) === EXCEL_PERSON_TYPES.CHILD_ONLY) {
      ratingInfo.dependents[0] = this.getGenericDependentAge20();
    }
    else if (county.state === 'NY') {
      const fullFamilyGroups = this.gridHelper.getFullFamilyFormGroupsBySubscriberStateId(stateId);
      
      // Only care about dependent ages
      for (let i = 1; i < fullFamilyGroups.length; i++) {
        const dependentDOB = fullFamilyGroups[i].formGroups[this.gridHelper.getSubscriberFormKey(fullFamilyGroups[i].stateId)].controls.dob.value;
        const dependentAge = moment().diff(moment(dependentDOB), 'years');

        if (dependentAge > 26) {
          ratingInfo.dependents[0] = this.getGenericDependentAge29();
          break;
        }
        else {
          ratingInfo.dependents[0] = this.getGenericDependentAge20();
        }
      }
    }

    return ratingInfo;
  }

  getGenericSubscriber(): Person {
    return {
      dob: moment().subtract(20, 'years').format('YYYY-MM-DD'),
      gender: 'M',
      personId: 0,
      relationship: APPLICANT_RELATION.SUBSCRIBER,
      tobaccoIndicator: false
    }
  }

  getGenericDependentAge20(): Person {
    return {
      dob: moment().subtract(20, 'years').format('YYYY-MM-DD'),
      gender: 'M',
      personId: 0,
      relationship: APPLICANT_RELATION.DEPENDENT,
      tobaccoIndicator: false
    }
  }

  getGenericDependentAge29(): Person {
    return {
      dob: moment().subtract(29, 'years').format('YYYY-MM-DD'),
      gender: 'M',
      personId: 0,
      relationship: APPLICANT_RELATION.DEPENDENT,
      tobaccoIndicator: false
    }
  }

  /**
   * 
   * @param entry - The formsAsJson entry object
   * @param formControl - The form control tied to the entry
   * 
   * @description 
    The excel cell formatting on the date fields is supposed to be "text" formatting. The user can change the
    default formatting to "date", which is most likely from copying and pasting from another sheet.
    When this happens, instead of a string, the parsed value is a 5 digit number representing days since January 1, 1900.
   */
  transformExcelDateToMoment(entry: MassUploadEntryConfig, formControl: AbstractControl) {
    let defaultValue = entry.formControlAttributes.setDefaultValue as any;
    if (defaultValue && !isNaN(defaultValue) && defaultValue.toString().length === 5) {
      let momentDate = moment('1900-01-01').add(+entry.formControlAttributes.setDefaultValue - 2, 'days');
      entry.formControlAttributes.setDefaultValue = momentDate.format(SUBMISSION_DATE_FORMAT);
      formControl.setValue(momentDate);
    }
  }
}
